import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { IconCell, ViewActionTableCell } from '@components/common';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell';

import { IDashboardAssetTypeSummary } from '@interfaces/platform/platformDashboard';

import { columnSizes } from '@constants/table';

interface IAcquireAssetsColumns {
	openOrganizationsTable: (asset: IDashboardAssetTypeSummary) => void;
	isSingleOrganization?: boolean;
}

export const getAssetTypesColumns = ({
	openOrganizationsTable,
	isSingleOrganization,
}: IAcquireAssetsColumns) => ([
	{
		header: 'Asset Type',
		accessorKey: 'assetTypeName',
		cell: ({ row }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const value = row.original?.type.assetTypeName;
			const id = row.original?.type?.icon?.id;

			return (
				<IconCell
					id={id}
					text={value}
				/>
			);
		},
	},
	{
		header: 'Asset Subtype',
		accessorKey: 'assetSubTypeName',
		enableSorting: false,
		cell: ({ row }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const value = row.original?.subType?.assetSubTypeName;

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'Quantity',
		accessorKey: 'quantity',
		size: columnSizes.size160,
		isHidden: isSingleOrganization,
		cell: ({ row }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const value = row.original?.quantity;

			return (
				<AdaptiveTableCell
					isCentered
					text={value ? value.toString() : '0'}
				/>
			);
		},
	},
	{
		header: 'Used in organisations',
		accessorKey: 'usedInOrganizations',
		size: columnSizes.size160,
		isHidden: isSingleOrganization,
		cell: ({ row }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const id = row.original?.type.id;
			const value = row.original?.usedInOrganizations;

			const onClick = () => {
				if (!id) return;

				openOrganizationsTable(row.original);
			};

			return (
				<ViewActionTableCell
					isCentered
					isDisabled={!value}
					onClick={onClick}
					text={value ? value.toString() : '0'}
					tooltipLabel='Details'
				/>
			);
		},
	},
	{
		header: 'All assets',
		accessorKey: 'allAssets',
		isHidden: !isSingleOrganization,
		cell: ({ getValue }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell
					isCentered
					text={value}
				/>
			);
		},
	},
	{
		header: () => <>Shown on<br />the map</>,
		accessorKey: 'showedAssets',
		isHidden: !isSingleOrganization,
		cell: ({ getValue }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell
					isCentered
					text={value}
				/>
			);
		},
	},
	{
		header: 'Hidden',
		accessorKey: 'hiddenAssets',
		isHidden: !isSingleOrganization,
		cell: ({ getValue }: CellContext<IDashboardAssetTypeSummary, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell
					isCentered
					text={value}
				/>
			);
		},
	},
]);
